// Primer paso del formulario de creación de sucursal - Argentina
// Incluye todos los campos: persona jurídica/física, CUIT, AFIP, provincia y ciudad

import { FormControlLabel, FormHelperText, IconButton, Radio, RadioGroup, Switch, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocationSelectors } from "../../../../LocationSelectors";
import InfoIcon from "../../../../icons/InfoIcon";
import { BranchSettings } from "../../../../../services/dto/request/branches-request-dto";
import { BranchBasicInfo } from "../../../../../services/dto/request/branches-request-dto";
import { BranchStatus } from "../../../../../constants/branch-status";
import { sanitizeArgBranchNameInput } from "../../../../../utils/helpers";
import { KycFieldsAR } from "./kyc-fields-ar";

const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
  "& .Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiFormLabel-root.Mui-error": {
    color: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:before": {
    borderBottomColor: "#d32f2f",
  },
  "& .MuiInput-underline.Mui-error:after": {
    borderBottomColor: "#d32f2f",
  },
};

const switchStyles = {
  "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
    bgcolor: "#EF0BB8",
  },
  "& .MuiSwitch-track": {
    bgcolor: "#898989",
  },
  "& .MuiSwitch-thumb": {
    bgcolor: "#EF0BB8",
  },
};

export interface BranchInfoStepARProps {
  branchBasicInfo: BranchBasicInfo;
  branchSettings: BranchSettings;
  errors: Record<string, string>;
  handleBranchBasicInfoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBranchSettingsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>, type: "branchConfig" | "branchBasicInfo" | "manager") => void;
  withSplitTip: boolean;
  disabled: boolean;
  branchStatus?: BranchStatus;
  isEditMode?: boolean;
  existingFiles?: {
    afipFile: string | null;
    managerDniFront: string | null;
    managerDniBack: string | null;
    managerSelfie: string | null;
  };
}

/**
 * Formulario de información de sucursal para Argentina
 * Incluye todos los campos: persona jurídica/física, CUIT, AFIP, provincia y ciudad
 */
export function BranchInfoStepAR({
  branchBasicInfo,
  branchSettings,
  errors,
  handleBranchBasicInfoChange,
  handleBranchSettingsChange,
  handleSwitchChange,
  withSplitTip,
  disabled,
  branchStatus,
  isEditMode = false,
  existingFiles
}: BranchInfoStepARProps) {
  const { t } = useTranslation();
  const requireKycFields =
    branchSettings.createCBU &&
    (!isEditMode || branchStatus === BranchStatus.INCOMPLETE);

  return (
    <div className="flex flex-col gap-4">
      {/* Sucursales solo CLOVER no usan cuenta propinas Paycloud */}
      {branchSettings.createCBU && (
      <div className="w-full flex justify-between items-center border-b pb-4 mb-2">
        <div className="flex flex-col">
          <label htmlFor="createCBU">
            {t("branches.create.tip-account")}
          </label>
          <FormHelperText sx={{ fontFamily: "Montserrat" }}>
            {t("branches.create.tip-account-note")}
          </FormHelperText>
        </div>
        <Switch
          id="createCBU"
          name="createCBU"
          checked={true}
          onChange={(e) => handleSwitchChange(e, "branchConfig")}
          disabled={true}
          sx={switchStyles}
        />
      </div>
      )}
      <TextField
        type="text"
        name="name"
        value={branchBasicInfo.name}
        onChange={handleBranchBasicInfoChange}
        error={!!errors.name}
        helperText={errors.name}
        label={t("branches.create.name")}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          input.value = sanitizeArgBranchNameInput(input.value);
        }}
        variant="standard"
        disabled={disabled || isEditMode}
        required
        fullWidth
        sx={textFieldStyles}
      />

      {/* Campo nombre de fantasía - Solo en modo de edición */}
      {isEditMode && (
        <TextField
          type="text"
          name="fantasyName"
          value={branchBasicInfo.fantasyName || ""}
          onChange={handleBranchBasicInfoChange}
          error={!!errors.fantasyName}
          helperText={errors.fantasyName}
          label={t("branches.edit.fantasy-name")}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            input.value = sanitizeArgBranchNameInput(input.value);
          }}
          variant="standard"
          disabled={disabled}
          fullWidth
          sx={textFieldStyles}
        />
      )}
      <TextField
        type="text"
        name="address"
        value={branchBasicInfo.address}
        onChange={handleBranchBasicInfoChange}
        required
        disabled={disabled}
        error={!!errors.address}
        helperText={errors.address}
        label={t("branches.create.address")}
        variant="standard"
        sx={textFieldStyles}
      />
      <TextField
        type="text"
        name="phone"
        value={branchBasicInfo.phone}
        onChange={handleBranchBasicInfoChange}
        required
        error={!!errors.phone}
        helperText={errors.phone}
        label={t("branches.create.phone")}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;

          value = value.replace(/[^\d\s+]/g, ""); // Permitimos numeros, espacios y +
          value = value.replace(/\s+/g, " "); // No permitimos mas de 1 espacio consecutivo

          // Si ingresa + que sea lo primero de la cadena
          value = value.replace(/(?!^)\+/g, "");
          if (value.includes("+") && value[0] !== "+") {
            value = "+" + value.replace(/\+/g, "");
          }

          input.value = value;
        }}
        variant="standard"
        disabled={disabled}
        sx={textFieldStyles}
      />

      {branchSettings.createCBU && (
        <>
          <div className="grid grid-cols-2 gap-2 transition-all duration-300 ease-in-out">
            <TextField
              type="text"
              name="floor"
              error={!!errors.floor}
              helperText={errors.floor}
              value={branchBasicInfo.floor}
              onChange={handleBranchBasicInfoChange}
              label={t("branches.create.floor")}
              disabled={disabled}
              variant="standard"
              sx={textFieldStyles}
            />
            <TextField
              type="text"
              name="postalCode"
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              value={branchBasicInfo.postalCode}
              onChange={handleBranchBasicInfoChange}
              label={t("branches.create.postal-code")}
              required
              variant="standard"
              disabled={disabled}
              sx={textFieldStyles}
            />
          </div>

          <TextField
            type="text"
            name="department"
            error={!!errors.department}
            helperText={errors.department}
            value={branchBasicInfo.department}
            onChange={handleBranchBasicInfoChange}
            label={t("branches.create.department")}
            variant="standard"
            disabled={disabled}
            sx={textFieldStyles}
          />

          <div className="flex flex-col gap-1">
            <RadioGroup
              row
              name="personType"
              value={branchSettings.personType}
              onChange={(e) => handleBranchSettingsChange(e)}
              sx={{
                "& .MuiRadio-root": {
                  color: "#898989",
                  "&.Mui-checked": {
                    color: "#EF0BB8",
                  },
                },
                "& .MuiFormControlLabel-label": {
                  fontFamily: "Montserrat",
                  color: "#000",
                },
              }}
            >
              <FormControlLabel
                value="LEGAL"
                control={<Radio disabled={disabled} />}
                label={t("branches.create.legal-person")}
              />
              <FormControlLabel
                value="PHYSICAL"
                control={<Radio disabled={disabled} />}
                label={t("branches.create.physical-person")}
              />
            </RadioGroup>
          </div>

          {branchSettings.personType === "PHYSICAL" && (
            <div className="w-full flex justify-between items-center">
              <div className="flex flex-col">
                <label htmlFor="isTaxPayer" className="text-black">
                  {t("branches.create.is-tax-payer")}
                </label>
                <FormHelperText
                  sx={{
                    fontFamily: "Montserrat",
                  }}
                >
                  {t("branches.create.is-tax-payer-note")}
                </FormHelperText>
              </div>
              <Switch
                id="isTaxPayer"
                name="isTaxPayer"
                checked={branchSettings.isTaxPayer}
                onChange={(e) => handleSwitchChange(e, "branchConfig")}
                disabled={disabled}
                sx={switchStyles}
              />
            </div>
          )}

          <div className="flex text-black  items-end gap-4">
            <div className="flex flex-col gap-1 w-1/4">
              <label htmlFor="identityType" className="text-md text-black">
                {t("branches.create.identity-type")}
              </label>
              <select
                id="identityType"
                name="identityType"
                className={`w-full px-3 py-2 border rounded-md bg-white ${
                  errors.identityType
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-primary"
                } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                disabled={disabled}
                value={branchSettings.identityType}
                onChange={(e) =>
                  handleBranchSettingsChange(
                    e as unknown as React.ChangeEvent<HTMLInputElement>
                  )
                }
              >
                {branchSettings.personType === "LEGAL" ? (
                  <option value="CUIT">CUIT</option>
                ) : (
                  <option value="CUIL">CUIL</option>
                )}
              </select>
              {errors.identityType && (
                <span className="text-md text-red-500">
                  {errors.identityType}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1 flex-1">
              <TextField
                id="identityNumber"
                name="identityNumber"
                label={branchSettings.identityType}
                error={!!errors.identityNumber}
                helperText={errors.identityNumber}
                value={branchSettings.identityNumber}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^\d]/g, "")
                    .slice(0, 11);
                  e.target.value = value;
                  handleBranchSettingsChange(
                    e as unknown as React.ChangeEvent<HTMLInputElement>
                  );
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                  },
                }}
                required
                type="text"
                variant="standard"
                disabled={disabled}
                sx={textFieldStyles}
              />
            </div>
          </div>

          {requireKycFields && (
            <KycFieldsAR
              branchBasicInfo={branchBasicInfo}
              errors={errors}
              disabled={disabled}
              handleBranchBasicInfoChange={handleBranchBasicInfoChange}
              handleSwitchChange={handleSwitchChange}
            />
          )}

          {branchStatus !== BranchStatus.APPROVED && (
            <div className="w-full flex flex-col gap-2">
              <label htmlFor="afipFile" className="text-black">
                {t("branches.create.afip-file")}
              </label>
              <input
                type="file"
                id="afipFile"
                name="afipFile"
                accept=".pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleBranchBasicInfoChange({
                      target: {
                        name: "afipFile",
                        value: file,
                      },
                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md bg-white ${
                  errors.afipFile
                    ? "border-red focus:ring-red"
                    : "border-gray-300 focus:ring-primary"
                } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                disabled={disabled}
              />
              {errors.afipFile && (
                <span className="text-md text-red text-sm">
                  {errors.afipFile}
                </span>
              )}
              <FormHelperText sx={{ fontFamily: "Montserrat" }}>
                {t("branches.create.afip-file-note")}
              </FormHelperText>

              {/* Mostrar archivo AFIP cargado */}
              {(branchBasicInfo.afipFile || existingFiles?.afipFile) && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 font-medium">
                    {t("branches.edit.afip-file-loaded")}
                  </p>
                </div>
              )}
            </div>
          )}

          <LocationSelectors
            key="branch-location"
            initialDepartmentId={
              branchBasicInfo.branchProvinceId > 0
                ? branchBasicInfo.branchProvinceId.toString()
                : ""
            }
            initialCityId={
              branchBasicInfo.branchCityId > 0
                ? branchBasicInfo.branchCityId.toString()
                : ""
            }
            onProvinceChange={(provinceId) => {
              handleBranchBasicInfoChange({
                target: {
                  name: "branchProvinceId",
                  value: parseInt(provinceId),
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
            onCityChange={(cityId) => {
              handleBranchBasicInfoChange({
                target: { name: "branchCityId", value: parseInt(cityId) },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
          />
          {(errors.branchProvinceId || errors.branchCityId) && (
            <p className="text-red mt-2 text-xs">
              {errors.branchProvinceId || errors.branchCityId}
            </p>
          )}

          <div className="w-full flex justify-between items-center">
            <div className="flex flex-col">
              <label htmlFor="sameAddress" className="text-black">
                {t("branches.create.same-address")}
              </label>
              <FormHelperText sx={{ fontFamily: "Montserrat" }}>
                {t("branches.create.same-address-note")}
              </FormHelperText>
            </div>
            <Switch
              id="sameAddress"
              name="sameAddress"
              checked={branchBasicInfo.sameAddress}
              onChange={(e) => handleSwitchChange(e, "branchBasicInfo")}
              sx={switchStyles}
              disabled={disabled}
            />
          </div>

          <div
            className={`space-y-2 transition-all duration-300 ease-in-out  ${
              branchBasicInfo.sameAddress
                ? "max-h-0 opacity-0"
                : "max-h-[550px] opacity-100"
            }`}
          >
            <div className="flex items-center justify-center">
              <h3 className="text-base font-medium text-slate-500 text-center">
                {t("branches.create.fiscal-address")}
              </h3>
              <Tooltip
                title={
                  <p style={{ fontFamily: "Montserrat" }}>
                    {t("branches.create.fiscal-address-note")}
                  </p>
                }
              >
                <IconButton>
                  <InfoIcon sx={{ fill: "#fff", height: 16, width: 16 }} />
                </IconButton>
              </Tooltip>
            </div>

            <TextField
              type="text"
              name="activityAddress"
              error={!!errors.activityAddress}
              helperText={errors.activityAddress}
              value={branchBasicInfo.activityAddress}
              onChange={handleBranchBasicInfoChange}
              label={t("branches.create.fiscal-address")}
              variant="standard"
              fullWidth
              required={!branchBasicInfo.sameAddress}
              disabled={disabled}
              sx={textFieldStyles}
            />

            <div className="grid grid-cols-3 gap-2 pb-2">
              <TextField
                type="text"
                name="activityFloor"
                error={!!errors.activityFloor}
                helperText={errors.activityFloor}
                value={branchBasicInfo.activityFloor}
                onChange={handleBranchBasicInfoChange}
                label={t("branches.create.floor")}
                variant="standard"
                disabled={disabled}
                sx={textFieldStyles}
              />
              <TextField
                type="number"
                name="activityPostalCode"
                error={!!errors.activityPostalCode}
                helperText={errors.activityPostalCode}
                value={branchBasicInfo.activityPostalCode}
                onChange={handleBranchBasicInfoChange}
                label={t("branches.create.postal-code")}
                required={!branchBasicInfo.sameAddress}
                variant="standard"
                disabled={disabled}
                sx={textFieldStyles}
              />
              <TextField
                type="text"
                name="activityDepartment"
                error={!!errors.activityDepartment}
                helperText={errors.activityDepartment}
                value={branchBasicInfo.activityDepartment}
                onChange={handleBranchBasicInfoChange}
                label={t("branches.create.department")}
                variant="standard"
                disabled={disabled}
                sx={textFieldStyles}
              />
            </div>

            <LocationSelectors
              key="branch-activity-location"
              initialDepartmentId={
                branchBasicInfo?.activityProvinceId &&
                branchBasicInfo.activityProvinceId > 0
                  ? branchBasicInfo.activityProvinceId.toString()
                  : ""
              }
              initialCityId={
                branchBasicInfo?.activityCityId &&
                branchBasicInfo.activityCityId > 0
                  ? branchBasicInfo.activityCityId.toString()
                  : ""
              }
              onProvinceChange={(provinceId) => {
                handleBranchBasicInfoChange({
                  target: {
                    name: "activityProvinceId",
                    value: parseInt(provinceId),
                  },
                } as unknown as React.ChangeEvent<HTMLInputElement>);
              }}
              onCityChange={(cityId) => {
                handleBranchBasicInfoChange({
                  target: { name: "activityCityId", value: parseInt(cityId) },
                } as unknown as React.ChangeEvent<HTMLInputElement>);
              }}
            />
          </div>
        </>
      )}
      {withSplitTip && (
        <div className="w-full flex justify-between items-center">
          <div className="flex flex-col ">
            <label htmlFor="equalSplit">
              {t("branches.create.with-equal-split")}
            </label>
            <FormHelperText
              sx={{
                fontFamily: "Montserrat",
              }}
            >
              {t("branches.edit.split-helper")}
            </FormHelperText>
          </div>
          <Switch
            id="equalSplitTip"
            name="equalSplitTip"
            checked={branchSettings.equalSplitTip}
            onChange={(e) => handleSwitchChange(e, "branchConfig")}
            disabled={disabled}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                bgcolor: "#EF0BB8",
              },
              "& .MuiSwitch-track": {
                bgcolor: "#898989",
              },
              "& .MuiSwitch-thumb": {
                bgcolor: "#EF0BB8",
              },
            }}
          />
        </div>
      )}

      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col ">
          <label htmlFor="onlyRatings" className="text-black">
            {t("branches.create.only-review")}
          </label>
          <FormHelperText
            sx={{
              fontFamily: "Montserrat",
            }}
          >
            {t("branches.create.only-review-note")}
          </FormHelperText>
        </div>
        <Switch
          id="onlyRatings"
          name="onlyRatings"
          checked={branchSettings.onlyRatings}
          onChange={(e) => handleSwitchChange(e, "branchConfig")}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
              bgcolor: "#EF0BB8",
            },
            "& .MuiSwitch-track": {
              bgcolor: "#898989",
            },
            "& .MuiSwitch-thumb": {
              bgcolor: "#EF0BB8",
            },
          }}
        />
      </div>

      <div className="w-full flex flex-col relative">
        <div className="flex items-center gap-0.5">
          <span
            className="text-black font-normal text-base"
            style={{ fontFamily: "Montserrat" }}
          >
            {t("branches.create.google.title")}
          </span>
          <Tooltip
            title={
              <p style={{ fontFamily: "Montserrat" }}>
                {t("branches.create.google.tooltip")}
              </p>
            }
            placement="top"
          >
            <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}>
              <InfoIcon
                sx={{
                  fill: "#fff",
                  height: 16,
                  width: 16,
                }}
              />
            </IconButton>
          </Tooltip>
        </div>
        <TextField
          type="text"
          fullWidth
          disabled={disabled}
          placeholder="https://g.page/your-business/review"
          value={branchSettings.googleReviewLink ?? ""}
          onChange={(e) =>
            handleBranchSettingsChange(
              e as unknown as React.ChangeEvent<HTMLInputElement>
            )
          }
          variant="standard"
          name="googleReviewLink"
          sx={textFieldStyles}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open('https://support.google.com/business/answer/3474122?hl=es-419', '_blank');
          }}
          className="text-blue-600 hover:underline text-sm w-fit mt-1 text-left bg-transparent border-none p-0 cursor-pointer font-normal"
          style={{ zIndex: 1000 }}
        >
          {t("branches.create.google.link")}
        </button>
      </div>
    </div>
  );
}

